import { useCallback, useEffect, useRef, useState } from 'react';

import type { FossabotPRStatus } from '../../../../../shared/fossabot/common';

/*
 * ============================================================================
 * ⚠️  DEMO ONLY — FAKE HAPPY-PATH DRIVER. DELETE THIS FILE WHEN WIRING IS REAL.
 * ============================================================================
 * This is throwaway scaffolding so the "Fix with fossabot" box does something
 * when you click Create. It fakes the PR lifecycle with timers, walking the box
 * from 'none' through the happy path to 'merged'. It makes NO API calls.
 *
 * When the real status query (GET .../status) and the create-PR mutation
 * (POST .../create) land, delete this hook and drive FossabotFixBox from those
 * instead. Nothing else depends on it — the box itself is a pure presentational
 * component that takes status + urls + onCreatePR as props.
 * ============================================================================
 */

/** Ms between each fake step. Exported so the test can advance precisely. */
export const FAKE_STEP_MS = 1600;

const FAKE_VCS_URL = 'https://github.com/some-org/some-repo/pull/123';
const FAKE_FOSSABOT_URL = 'https://app.fossabot.com/prs/123';

/** Happy-path states after 'creating', in order. */
const HAPPY_PATH: FossabotPRStatus[] = ['queued', 'analyzing', 'needsReview', 'readyToMerge', 'merged'];

type FakeFossabotFlow = {
  status: FossabotPRStatus;
  vcsUrl: string | null;
  fossabotUrl: string | null;
  onCreatePR: () => void;
};

export const useFakeFossabotFlow = (): FakeFossabotFlow => {
  const [status, setStatus] = useState<FossabotPRStatus>('none');
  const timers = useRef<Array<ReturnType<typeof setTimeout>>>([]);

  const clearTimers = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  }, []);

  // Cancel any pending steps on unmount so we never setState after teardown.
  useEffect(() => clearTimers, [clearTimers]);

  const onCreatePR = useCallback(() => {
    clearTimers();
    setStatus('creating'); // immediate feedback on click
    timers.current = HAPPY_PATH.map((next, index) =>
      setTimeout(() => setStatus(next), FAKE_STEP_MS * (index + 1)),
    );
  }, [clearTimers]);

  // The fossabot job exists once creation starts; the PR opens one step later.
  const hasFossabotJob = status !== 'none';
  const hasOpenPr = hasFossabotJob && status !== 'creating';

  return {
    status,
    vcsUrl: hasOpenPr ? FAKE_VCS_URL : null,
    fossabotUrl: hasFossabotJob ? FAKE_FOSSABOT_URL : null,
    onCreatePR,
  };
};
